import { CGDeleteMode } from './DataSyncItem';

export const cgStatuses = [
  {text: 'Draft', value: 'DRAFT'},
  {text: 'Ready', value: 'READY'},
  {text: 'In Review', value: 'IN_REVIEW'},
  {text: 'Rejected', value: 'REJECTED'},
  {text: 'Scheduled', value: 'SCHEDULED'},
  {text: 'Published', value: 'PUBLISHED'},
  {text: 'Previously Published', value: 'PREVIOUSLY_PUBLISHED'}
];

export const cgLanguages = [
  {text: 'Neutral', value: 'NEUTRAL'},
  {text: 'Arabic', value: 'ar'},
  {text: 'Bulgarian', value: 'bg'},
  {text: 'Brazilian', value: 'br'},
  {text: 'Catalan', value: 'ca'},
  {text: 'CJK', value: 'cjk'},
  {text: 'German', value: 'de'},
  {text: 'Greek', value: 'el'},
  {text: 'English', value: 'en'},
  {text: 'Spanish', value: 'es'},
  {text: 'Basque', value: 'eu'},
  {text: 'Farsi', value: 'fa'},
  {text: 'Finnish', value: 'fi'},
  {text: 'French', value: 'fr'},
  {text: 'Galician', value: 'gl'},
  {text: 'Hindi', value: 'hi'},
  {text: 'Hungarian', value: 'hu'},
  {text: 'Armenian', value: 'hy'},
  {text: 'Indonesian', value: 'id'},
  {text: 'Italian', value: 'it'},
  {text: 'Japanese', value: 'ja'},
  {text: 'Korean', value: 'ko'},
  {text: 'Kurdish', value: 'ku'},
  {text: 'Latvian', value: 'lv'},
  {text: 'Dutch', value: 'nl'},
  {text: 'Norwegian', value: 'no'},
  {text: 'Polish', value: 'pl'},
  {text: 'Romanian', value: 'ro'},
  {text: 'Russian', value: 'ru'},
  {text: 'Swedish', value: 'sv'},
  {text: 'Thai', value: 'th'},
  {text: 'Turkish', value: 'tr'},
  {text: 'Ukrainian', value: 'uk'},
  {text: 'Chinese', value: 'zh'},
];

// A Content Graph locale: a known bare code, NEUTRAL, or a custom country-coded
// value such as "en-US". Kept as a plain string to allow custom locale entry.
export type CGLanguage = string;

// Canonical locale for each proto CGLanguage enum name. Used to read the legacy
// `languages` field of GetCGSourceMetadataResponse when the newer `locales` string
// field isn't present (e.g. during a rolling deploy against an older backend, which
// serialises the enum by name like "CG_LANGUAGE_ENGLISH").
export const cgLanguageEnumToLocale: { [enumName: string]: string } = {
  CG_LANGUAGE_NEUTRAL: 'NEUTRAL',
  CG_LANGUAGE_ARABIC: 'ar',
  CG_LANGUAGE_BULGARIAN: 'bg',
  CG_LANGUAGE_BRAZILIAN: 'br',
  CG_LANGUAGE_CATALAN: 'ca',
  CG_LANGUAGE_CJK: 'cjk',
  CG_LANGUAGE_GERMAN: 'de',
  CG_LANGUAGE_GREEK: 'el',
  CG_LANGUAGE_ENGLISH: 'en',
  CG_LANGUAGE_SPANISH: 'es',
  CG_LANGUAGE_BASQUE: 'eu',
  CG_LANGUAGE_FARSI: 'fa',
  CG_LANGUAGE_FINNISH: 'fi',
  CG_LANGUAGE_FRENCH: 'fr',
  CG_LANGUAGE_GALICIAN: 'gl',
  CG_LANGUAGE_HINDI: 'hi',
  CG_LANGUAGE_HUNGARIAN: 'hu',
  CG_LANGUAGE_ARMENIAN: 'hy',
  CG_LANGUAGE_INDONESIAN: 'id',
  CG_LANGUAGE_ITALIAN: 'it',
  CG_LANGUAGE_JAPANESE: 'ja',
  CG_LANGUAGE_KOREAN: 'ko',
  CG_LANGUAGE_KURDISH: 'ku',
  CG_LANGUAGE_LATVIAN: 'lv',
  CG_LANGUAGE_DUTCH: 'nl',
  CG_LANGUAGE_NORWEGIAN: 'no',
  CG_LANGUAGE_POLISH: 'pl',
  CG_LANGUAGE_ROMANIAN: 'ro',
  CG_LANGUAGE_RUSSIAN: 'ru',
  CG_LANGUAGE_SWEDISH: 'sv',
  CG_LANGUAGE_THAI: 'th',
  CG_LANGUAGE_TURKISH: 'tr',
  CG_LANGUAGE_UKRAINIAN: 'uk',
  CG_LANGUAGE_CHINESE: 'zh',
};

// Normalizes a GetCGSourceMetadata response to canonical locale strings. Prefers the
// newer `locales` field and falls back to the legacy `languages` enum names, so the UI
// works against both old and new backends during a rolling deploy. Unknown enum names
// are dropped; anything already in code form is passed through.
export function sourceMetadataLocales(
  body: { locales?: string[]; languages?: string[] } | null | undefined
): string[] {
  if (body?.locales?.length) {
    return body.locales;
  }
  if (body?.languages?.length) {
    return body.languages
      .map((name) => cgLanguageEnumToLocale[name] ?? (name.startsWith('CG_LANGUAGE_') ? undefined : name))
      .filter((code): code is string => !!code);
  }
  return [];
}

// Human-readable name for a locale code. Prefers the curated cgLanguages list
// (covers the known bare codes plus special values like NEUTRAL/cjk that Intl
// can't name). Falls back to Intl.DisplayNames so custom country-coded locales
// such as "en-US" render as "American English" instead of the raw code, and to
// the code itself when the value isn't a valid BCP-47 locale.
export function localeDisplayName(code: string): string {
  const known = cgLanguages.find((language) => language.value === code);
  if (known) {
    return known.text;
  }
  try {
    return new Intl.DisplayNames(['en'], {type: 'language'}).of(code) ?? code;
  } catch {
    return code;
  }
}

export const cgDeleteModes = [
  {text: 'None', value: ''},
  {text: 'Soft', value: CGDeleteMode.SOFT_DELETE},
  {text: 'Hard', value: CGDeleteMode.HARD_DELETE}
];
